import { PixStatus } from '@portal/enum/PixStatus';
import type { PendingPixKeyPortability } from '@portal/models/module';
import { getInstance } from '@portal/repositories/securitySdkConfig';
import { getMetadata } from '@portal/services/metadata';

const URL = '/pix';
const URL_V2 = '/v2/pix';

interface RegisterPixKeyResponse {
  key: string;
  keyType: number;
  status: PixStatus;
  statusString: string;
  idReivindication: string;
  requiresPortability: boolean;
  requiresOwnershipClaim: boolean;
  type: number;
  protocolId: string;
}

const PixApi = {
  confirmPortability: async (body: models.RegisterPixKey) => {
    const instance = getInstance();
    const { data } = await instance.post(URL_V2, body);

    return data;
  },

  registerPixKey: async (body: models.RegisterPixKey) => {
    const instance = getInstance();
    const { data } = await instance.post<RegisterPixKeyResponse>(URL_V2, body);

    return data;
  },

  getAllPixKeys: async () => {
    const instance = getInstance();
    const { data } = await instance.get(URL);
    return data;
  },

  deletePortability: async (key: string) => {
    const instance = getInstance();
    await instance.delete(`${URL}/${encodeURIComponent(key)}/portability`);
  },

  portabilityResponse: async (key: string, confirm: boolean) => {
    const instance = getInstance();
    const { deviceInfo } = await getMetadata();
    await instance.post(
      `${URL}/${encodeURIComponent(key)}/portability-response`,
      { confirm, deviceInfo }
    );
  },

  deletePixKey: async (key: string) => {
    const instance = getInstance();
    await instance.delete(`${URL}/${encodeURIComponent(key)}`);
  },

  decodePixQrCode: async (content: string) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      '/transaction/decode-pix-qrcode',
      {
        content,
      }
    );

    return data;
  },

  getPixInfo: async (key: string) => {
    const instance = getInstance();
    const { data } = await instance.get<bank.PixInfo>(`${URL}/pix-info`, {
      params: {
        key,
      },
    });

    return data;
  },

  getPSPs: async () => {
    const instance = getInstance();
    const { data } = await instance.get<bank.Psp[]>(`${URL}/psps`);

    return data;
  },

  getPixSetup: async () => {
    const instance = getInstance();
    const { data } = await instance.get<{
      pendingPortabilities: PendingPixKeyPortability[];
    }>(`${URL}/setup`);

    return data;
  },

  portabilityConfirm: async (key: string, confirm: boolean) => {
    const instance = getInstance();
    await instance.post(
      `${URL}/${encodeURIComponent(key)}/portability-confirm`,
      { confirm }
    );
  },
};

export default PixApi;
